import { memo } from 'react'
import {
  ChatListItemProps,
  WorkspaceChatListItemProps,
} from './chat-list-item.types'
import { useWorkspaceById } from '$logic/workspace'
import { Divider, styled, Typography, useTheme } from '@mui/material'
import { useLongPress } from '$hooks/use-long-press'
import { nothing } from '$helper/functions/utils'
import { Groups } from '@mui/icons-material'
import { AvatarBase, AvatarSize, HallAvatar } from '$common/avatar'
import { UnseenChip } from '$common/chip'
import { toLocale } from '$helper/functions/text'
import { WorkspaceAvatar } from '$common/avatar/workspace-avatar/workspace-avatar'
import { t } from '$i18n'
import { useWorkspacesUnseenCountByWorkspaceId } from '$logic/room-seen/react-hook'
import { WorkspaceRoomListItem } from '$logic/room-list'

export const WorkspaceChatListItem = memo<WorkspaceChatListItemProps>(
  function WorkspaceChatListItemComponent({
    pinned,
    isOnTop,
    selected,
    onClick,
    onContextMenu,
    onLongPress,
    workspaceId,
    workspaceChatIds,
    isOpened,
  }: WorkspaceChatListItemProps) {
    const workspace = useWorkspaceById(workspaceId!)
    const longPressHandlers = useLongPress(onLongPress?.('chatId') || nothing)

    const unseen = useWorkspacesUnseenCountByWorkspaceId(workspaceId)
    const theme = useTheme()

    return (
      <>
        <button
          style={{
            display: 'flex',
            padding: '8px',
            width: '100%',
            height: '88px',
            backgroundColor: selected
              ? theme.palette.primary100
              : isOpened
                ? 'rgba(80, 91, 95, 0.10)'
                : pinned || isOnTop
                  ? theme.palette.primary50 // Pinned Color
                  : theme.palette.white,
          }}
          type="button"
          className="chat-list-item--root"
          onClick={() =>
            onClick(workspaceId, 'workspace' as WorkspaceRoomListItem['type'])
          }
          onContextMenu={onContextMenu(workspaceId)}
          //  eslint-disable-next-line react/jsx-props-no-spreading
          {...longPressHandlers}
        >
          <Container color={workspace?.color}>
            <InfoBox>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Groups sx={{ color: 'neutral000', opacity: 0.5 }} />
                <Typography
                  variant="subtitle2"
                  color="neutral000"
                  alignSelf="center"
                >
                  {workspace?.name}
                </Typography>
              </div>

              {workspaceChatIds?.length ? (
                <HallAvatarGroup
                  hallIds={workspaceChatIds.map(({ chatId }) => chatId)}
                  borderColor={workspace?.color}
                />
              ) : null}
            </InfoBox>
            <WorkspaceAvatar
              workspaceId={workspaceId!}
              size={AvatarSize.SM}
              showBackdrop
            />

            {!!unseen && (
              <UnseenChip
                sx={{
                  zIndex: 3,
                  position: 'absolute',
                  left: '20px',
                  bottom: '14px',
                }}
                color="success500"
                label={
                  unseen > 99 ? t('chatList.tooManyUnseen') : toLocale(unseen)
                }
                withBorder
              />
            )}
          </Container>
        </button>
      </>
    )
  },
)

export function HallAvatarGroup({
  hallIds,
  borderColor,
}: {
  hallIds: string[]
  borderColor: string
}) {
  return (
    <Root>
      {hallIds.length > 3 ? (
        <AvatarBase
          children={
            <Typography
              variant="caption1"
              color="neutral800"
            >{`${toLocale(hallIds.length - 3)}+`}</Typography>
          }
          sx={{ border: '1px solid', borderColor }}
          size={AvatarSize._3XS}
        />
      ) : null}

      {hallIds
        .slice(0, 3)
        .reverse()
        .map(hallId => (
          <HallAvatar
            sx={{ border: '1px solid', borderColor }}
            roomId={hallId}
            size={AvatarSize._3XS}
          />
        ))}
    </Root>
  )
}

const Root = styled('div')`
  & > :not(:last-child) {
    margin-right: -4px;
  }
  display: flex;
  flex-direction: row-reverse;
  width: fit-content;
`

const InfoBox = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 4px;
  direction: rtl;
  width: 100%;
`

const Container = styled('div')<{ color: string }>`
  background-color: ${({ color }) => color};
  width: 100%;
  height: 100%;
  border-radius: 12px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
`
